/**
 * Orders every read of a case's transcript, narrow or full. Reads can finish
 * out of order: the refetch coordinator stops waiting on a slow read and starts
 * the next one, and mutations refetch GetIssue on their own schedule. Each read
 * takes a number when it starts. One that finishes behind a newer read keeps
 * its new rows but not its snapshot, so a late result cannot overwrite what a
 * newer read already wrote.
 *
 * Full GetIssue reads are ordered only for a case whose screen enabled it,
 * which only the streaming-v2 path does.
 */
type Order = { started: number; applied: number };

const orders = new Map<string, Order>();
const enabled = new Map<string, number>();

const orderOf = (issueId: string): Order => {
  const order = orders.get(issueId) ?? { started: 0, applied: 0 };
  orders.set(issueId, order);
  return order;
};

export const startRead = (issueId: string): number => {
  const order = orderOf(issueId);
  order.started += 1;
  return order.started;
};

/** True when a read that started later has already been applied. */
export const isStaleRead = (issueId: string, readNumber: number): boolean =>
  readNumber < orderOf(issueId).applied;

export const markReadApplied = (issueId: string, readNumber: number): void => {
  const order = orderOf(issueId);
  order.applied = Math.max(order.applied, readNumber);
};

/** Orders full GetIssue reads for the case until the returned function runs. */
export const enableFullReadOrdering = (issueId: string): (() => void) => {
  enabled.set(issueId, (enabled.get(issueId) ?? 0) + 1);
  return () => {
    const count = (enabled.get(issueId) ?? 1) - 1;
    if (count > 0) {
      enabled.set(issueId, count);
    } else {
      enabled.delete(issueId);
    }
  };
};

export const isFullReadOrderingEnabled = (issueId: string): boolean =>
  enabled.has(issueId);
